import React from "react";
import {
  Box,
  Button,
  Checkbox,
  IconButton,
  TableCell,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Email, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { toast } from "../../../../utils/notifications";
import { formatSingaporeDate, formatSingaporeTime } from "../../../../utils/timezone";
import StatusBadge from "../StatusBadge";
import { StatusActions } from "../UI";
import type { RFQ } from "../../../../types/rfq";

interface RfqSummaryRowProps {
  rfq: RFQ;
  expanded: boolean;
  isSelected: boolean;
  allItemsValidated: boolean;
  onToggleExpanded: () => void;
  onSelect: (rfqId: number) => void;
  onDelete: () => void;
  onViewAttachments: (rfqId: number) => void;
  onBlacklist: () => void;
  onOpenEmailDialog: () => void;
}

/** The received date and time; a click copies the email subject. */
function ReceivedCell({ rfq }: { rfq: RFQ }) {
  return (
    <TableCell>
      <Tooltip
        title={
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1, p: 1 }}>
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              Email Subject:
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              {rfq.original_subject || "No subject available"}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Click the date to copy the email subject
            </Typography>
          </Box>
        }
        arrow
        enterDelay={500}
        leaveDelay={200}
        sx={{ cursor: 'help' }}
      >
        <Box
          sx={{
            display: "inline-flex",
            flexDirection: "column",
            cursor: 'pointer',
            mx: -0.5,
            px: 0.5,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            }
          }}
          onClick={(e) => {
            e.stopPropagation();
            navigator.clipboard.writeText(rfq.original_subject || "No subject available");
            toast({
              title: "Copied!",
              description: "Email subject copied to clipboard",
              severity: "success"
            });
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'nowrap' }}>
            {formatSingaporeDate(rfq.rfq_received_date)}
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
            {formatSingaporeTime(rfq.rfq_received_date)}
          </Typography>
        </Box>
      </Tooltip>
    </TableCell>
  );
}

/** One RFQ's row in the list: select, expand, client, received, status, actions. */
export default function RfqSummaryRow({
  rfq,
  expanded,
  isSelected,
  allItemsValidated,
  onToggleExpanded,
  onSelect,
  onDelete,
  onViewAttachments,
  onBlacklist,
  onOpenEmailDialog,
}: RfqSummaryRowProps) {
  const handleCheckboxClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onSelect(rfq.rfq_id);
  };

  return (
    <TableRow
      sx={(theme) => ({
        cursor: "pointer",
        "&:hover": { bgcolor: expanded ? alpha(theme.palette.primary.main, 0.08) : "action.hover" },
        ...(expanded && {
          bgcolor: alpha(theme.palette.primary.main, 0.06),
          // Accent bar ties the header row to its item panel below
          "& > td:first-of-type": { boxShadow: `inset 3px 0 0 ${theme.palette.primary.main}` },
        }),
        "& > td": { py: 1.25, ...(expanded && { borderBottomColor: alpha(theme.palette.primary.main, 0.16) }) },
      })}
      onClick={(e) => {
        if (e.target &&
            (e.target as HTMLElement).closest('button, a, .MuiIconButton-root, .MuiCheckbox-root')) {
          return;
        }
        onToggleExpanded();
      }}
      role="checkbox"
      aria-checked={isSelected}
      selected={isSelected}
    >
      <TableCell padding="checkbox">
        <Checkbox
          checked={isSelected}
          onClick={handleCheckboxClick}
          sx={{ ml: 0.5 }}
        />
      </TableCell>
      <TableCell sx={{ whiteSpace: "nowrap" }}>
        <Box display="flex" alignItems="center" gap={1}>
          <IconButton
            size="small"
            onClick={onToggleExpanded}
            aria-expanded={expanded}
            sx={{
              color: expanded ? "primary.main" : "text.secondary",
              bgcolor: expanded ? "background.paper" : "transparent",
              border: 1,
              borderColor: expanded ? "primary.light" : "transparent",
              transition: "all 0.2s",
            }}
          >
            {expanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          </IconButton>
          <Typography variant="body2" fontWeight={600} sx={{ fontVariantNumeric: "tabular-nums" }}>
            {rfq.rfq_id}
          </Typography>
        </Box>
      </TableCell>
      <TableCell>
        <Typography variant="subtitle1" sx={{ lineHeight: 1.35, mb: 0.25 }}>
          {rfq.client_name || "Unknown"}
        </Typography>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: "anywhere" }}>
            {rfq.client_email || "No email"}
          </Typography>
          {rfq.excel_contact_email && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ overflowWrap: "anywhere" }}>
              {rfq.excel_contact_email}
            </Typography>
          )}
      </TableCell>
      <ReceivedCell rfq={rfq} />
      <TableCell sx={{ display: { xs: "none", md: "table-cell" } }}>
        <StatusBadge status={rfq.status} />
      </TableCell>
      <TableCell>
        {/* This width sets the shrink-to-fit Actions column below xl;
            anything beyond it wraps onto a second line. */}
        <Box sx={{ display: "flex", columnGap: 1, rowGap: 1, alignItems: "center", flexWrap: { xs: "wrap", xl: "nowrap" }, width: { xs: 250, lg: 500, xl: "auto" } }}>
          <StatusActions
            rfqId={rfq.rfq_id}
            status={rfq.status as "NEW" | "DIY" | "QUOTED"}
            allItemsValidated={allItemsValidated}
            onDelete={onDelete}
            onViewAttachments={onViewAttachments}
            onBlacklist={onBlacklist}
          />

          {/* Send Quote Button - moved from expanded view to main row */}
          {allItemsValidated && rfq.status !== "QUOTED" && (rfq.items || []).length > 0 && (
            <Button
              variant="contained"
              color="primary"
              size="small"
              startIcon={<Email />}
              onClick={onOpenEmailDialog}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Create Email
            </Button>
          )}
        </Box>
      </TableCell>
    </TableRow>
  );
}
